/**
 * Frontend NDJSON client for the Unified Agentic Pipeline v2.
 *
 * Uses fetch + ReadableStream for streaming NDJSON from Lambda Function URL.
 * Includes adaptive polling for async jobs, cancel, and event replay.
 *
 * Spec 01 §10.3-§10.5
 */

import { getAuthToken } from '@/lib/api'
import type { Scope } from '@/types/scope'

// ===================
// Types
// ===================

export type AgentIntent = 'chat' | 'plan' | 'write'

export type StateDefault = 'brainstorm' | 'draft' | 'revise' | 'chat'

export type EventType =
  | 'turn_started'
  | 'status'
  | 'evidence_search_started'
  | 'evidence_search_completed'
  | 'evidence_review_started'
  | 'evidence_review_completed'
  | 'citation_quotes'
  | 'tool_call_started'
  | 'tool_call_result'
  | 'assistant_message_chunk'
  | 'assistant_suggested_actions'
  | 'context_summarized'
  | 'context_trimmed'
  | 'plan_patch_proposed'
  | 'plan_patch_applied'
  | 'draft_patch_proposed'
  | 'draft_patch_applied'
  // R1 W1: an AI edit the user asked for is saved and can be undone:
  // { segmentId, sectionId, heading, version, proposalId }. Precedes the
  // `draft_patch_applied` carrying the saved segments.
  | 'draft_edit_applied'
  | 'async_handoff'
  // Excel schedule fill — the agent staged cell-fill proposals for an artifact.
  // Mirrors the backend union in backend/src/lib/agent-events.ts; the two must
  // stay in step or the event is silently dropped by the store's switch.
  | 'fill_proposals_staged'
  // Bid memory — `remember` saved an item: { memoryId, bidId, content }. The
  // panel shows "Saved to memory · Undo" under the reply (agent-store).
  | 'memory_saved'
  // R1 Q1: `ask_user` saved durable questions: { questions: PackAsk[] }. The
  // panel shows each as a question block (questions-store).
  | 'question_raised'
  // R1 Q2: a turn that carried `askAnswer` recorded it: { askId, outcome }
  // (QuestionAnswerOutcome). Right after turn_started.
  | 'question_answered'
  // R1 F1′: `start_job` queued a whole-document job and the turn ends now:
  // { jobId, artifactId, title, targets }. The job shows on its own card
  // (PanelDocumentJob, fed by the jobs list); it is never polled as the turn's.
  | 'job_started'
  // Quick-write artifact — a standalone document streams into the side panel.
  | 'artifact_started'
  | 'artifact_chunk'
  | 'artifact_completed'
  | 'verification_results'
  | 'coverage_check_started'
  | 'coverage_check_passed'
  | 'coverage_warning'
  | 'coverage_blocked'
  | 'heartbeat'
  | 'turn_completed'
  | 'turn_error'

export interface StreamEvent {
  v: number
  seq: number
  eventType: EventType
  turnId: string
  sessionId: string
  intentSelected: AgentIntent
  timestamp: string
  data: Record<string, unknown>
}

/**
 * Bid-scoped chat lane scope (tender chat). The orchestrator accepts this in
 * addition to the editor's unit/artifact scopes; kept as an additive union so
 * existing unit/artifact callers are unaffected.
 */
export interface BidChatScope {
  kind: 'bid'
  bidId: string
}

/**
 * Org-scoped chat lane scope (Assistant door). Library-only by default; an
 * optional per-turn `bidId` grant (set from the Sources picker) widens the turn
 * to also search that bid's tender documents (backend `requireBidAccess`-gated).
 * Additive union member — unit/artifact/bid callers are unaffected.
 */
export interface OrgChatScope {
  kind: 'org'
  bidId?: string
}

/** Exactly one of `answer` and `notKnown`. `effect` defaults to answer only. */
export interface TurnAskAnswer {
  askId: string
  answer?: string
  notKnown?: true
  effect?: 'answer_only' | 'update_draft'
}

export interface AgentTurnRequest {
  sessionId: string
  threadId: string
  scope: Scope | BidChatScope | OrgChatScope
  message: string
  idempotencyKey: string
  expectedRevision?: string
  suggestedOfferId?: string
  suggestedActionId?: string
  suggestedActionSourceTurnId?: string
  // Layer 1: answer-time Fact Bank scope (chat/plan turns)
  factBankOnly?: boolean
  selectedDocumentIds?: string[]
  /**
   * Passage the user highlighted in the artifact panel, sent as context for
   * this turn ("rewrite this bit"). Trimmed to 4000 chars by the backend.
   */
  selection?: { text: string }
  /**
   * R1 Q1/Q2: this turn answers one of the AI's durable questions. The backend
   * records the answer as the user before the turn runs, and the model reads
   * it as plain text ("Answer to your question …").
   */
  askAnswer?: TurnAskAnswer
  attachments?: Array<{
    attachmentId: string
    s3Key: string
    filename: string
    mimeType: string
    size: number
  }>
}

export type AgentEventHandler = (event: StreamEvent) => void

export interface AsyncJobResult {
  jobId: string
  status: 'completed' | 'failed' | 'cancelled'
  progress: number
  resultPayload: Record<string, unknown> | null
  errorMessage: string | null
}

// ===================
// Configuration
// ===================

const AGENT_STREAM_URL = import.meta.env['VITE_AGENT_STREAM_URL'] ?? ''
const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? '/api'
const STREAM_IDLE_TIMEOUT_MS = 45_000

// Adaptive polling intervals (Spec 01 §10.3)
const POLL_INTERVAL_FAST_MS = 2_000
const POLL_INTERVAL_MEDIUM_MS = 5_000
const POLL_INTERVAL_SLOW_MS = 10_000
const POLL_FAST_COUNT = 10
const POLL_MEDIUM_COUNT = 20
const MAX_CONSECUTIVE_POLL_ERRORS = 5

// ===================
// Auth Helper
// ===================

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// ===================
// NDJSON Streaming
// ===================

/**
 * Send a turn request and stream NDJSON events from the Lambda Function URL.
 *
 * Uses fetch + ReadableStream + TextDecoderStream for real-time parsing.
 * Handles partial lines gracefully.
 */
export async function sendAgentTurn(
  request: AgentTurnRequest,
  onEvent: AgentEventHandler,
  signal?: AbortSignal
): Promise<void> {
  const headers = await getAuthHeaders()

  // Create timeout controller (30s timeout)
  const timeoutController = new AbortController()
  const timeoutId = setTimeout(() => {
    timeoutController.abort()
  }, 30_000) // 30 seconds

  // Combine user signal with timeout signal
  const combinedSignal = signal
    ? combineAbortSignals([signal, timeoutController.signal])
    : timeoutController.signal

  try {
    const response = await fetch(AGENT_STREAM_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: combinedSignal,
    })

    // Clear timeout on successful response
    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new AgentClientError(
        'STREAM_ERROR',
        `Agent turn request failed: ${response.status} ${errorText}`
      )
    }

    if (!response.body) {
      throw new AgentClientError('NO_BODY', 'Response body is null')
    }

    // Parse NDJSON stream
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
    let buffer = ''
    let streamIdleTimedOut = false
    let idleTimer: ReturnType<typeof setTimeout> | null = null
    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer)
      idleTimer = setTimeout(() => {
        streamIdleTimedOut = true
        void reader.cancel('stream_idle_timeout')
      }, STREAM_IDLE_TIMEOUT_MS)
    }
    resetIdleTimer()

    try {
      for (;;) {
        const { done, value } = await reader.read()

        if (done) break
        resetIdleTimer()

        buffer += value
        const lines = buffer.split('\n')
        // Keep the last (potentially incomplete) line in the buffer
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (trimmed.length === 0) continue

          try {
            const event = JSON.parse(trimmed) as StreamEvent
            onEvent(event)
          } catch {
            // Skip malformed lines — may be partial chunk boundaries
          }
        }
      }

      // Process any remaining buffer content
      if (buffer.trim().length > 0) {
        try {
          const event = JSON.parse(buffer.trim()) as StreamEvent
          onEvent(event)
        } catch {
          // Incomplete final line — discard
        }
      }

      if (streamIdleTimedOut) {
        throw new AgentClientError(
          'STREAM_IDLE_TIMEOUT',
          'No reply came back. Please try again.'
        )
      }
    } finally {
      if (idleTimer) clearTimeout(idleTimer)
      reader.releaseLock()
    }
  } catch (error) {
    clearTimeout(timeoutId)

    // Check if timeout triggered the abort
    if (timeoutController.signal.aborted && !(signal?.aborted)) {
      throw new AgentClientError('TIMEOUT', 'Request timed out after 30 seconds')
    }

    // Abort initiated by caller (cancel, scope switch, reset)
    if (signal?.aborted) {
      throw new AgentClientError('CANCELLED', 'Operation was cancelled')
    }

    // Transport dropped mid-stream (e.g. Function URL stream reset)
    if (isStreamAbortError(error)) {
      throw new AgentClientError(
        'STREAM_ABORTED',
        'Connection interrupted while receiving AI response. Please try again.'
      )
    }

    // Re-throw other errors
    throw error
  }
}

// ===================
// Async Job Polling
// ===================

/**
 * Poll an async job with adaptive intervals.
 *
 * Polling schedule:
 *   - First 10 polls: every 2s
 *   - Next 20 polls: every 5s
 *   - After that: every 10s
 *   - Runs until the job reaches a terminal status or the caller aborts.
 */
export async function pollAsyncJob(
  jobId: string,
  sessionId: string,
  onProgress: (progress: number, message: string) => void,
  signal?: AbortSignal
): Promise<AsyncJobResult> {
  const headers = await getAuthHeaders()
  let pollCount = 0
  let consecutiveErrors = 0

  for (;;) {
    // Check abort
    if (signal?.aborted) {
      throw new AgentClientError('CANCELLED', 'Polling was cancelled')
    }

    // Adaptive interval
    let intervalMs: number
    if (pollCount < POLL_FAST_COUNT) {
      intervalMs = POLL_INTERVAL_FAST_MS
    } else if (pollCount < POLL_FAST_COUNT + POLL_MEDIUM_COUNT) {
      intervalMs = POLL_INTERVAL_MEDIUM_MS
    } else {
      intervalMs = POLL_INTERVAL_SLOW_MS
    }

    // Wait before polling
    await sleep(intervalMs, signal)

    pollCount++

    // Fetch job status
    const url = new URL(`${API_BASE_URL}/agent/jobs/${jobId}`, window.location.origin)
    url.searchParams.set('sessionId', sessionId)

    let response: Response
    try {
      response = await fetch(url.toString(), {
        method: 'GET',
        headers,
        ...(signal ? { signal } : {}),
      })
    } catch (error) {
      if (signal?.aborted) {
        throw new AgentClientError('CANCELLED', 'Polling was cancelled')
      }

      consecutiveErrors++
      if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        const message = error instanceof Error ? error.message : 'Unknown network error'
        throw new AgentClientError('POLL_FAILED', `Async polling failed repeatedly: ${message}`)
      }
      continue
    }

    if (!response.ok) {
      if (response.status === 404) {
        throw new AgentClientError('JOB_NOT_FOUND', 'Async job not found or expired')
      }
      if (response.status === 401 || response.status === 403) {
        throw new AgentClientError('UNAUTHORIZED', 'Authentication required for async polling')
      }

      const retryable =
        response.status === 408 ||
        response.status === 425 ||
        response.status === 429 ||
        response.status >= 500

      if (!retryable) {
        const errorText = await response.text().catch(() => 'Unknown error')
        throw new AgentClientError(
          'POLL_FAILED',
          `Async polling failed: ${response.status} ${errorText}`
        )
      }

      consecutiveErrors++
      if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        throw new AgentClientError(
          'POLL_FAILED',
          `Async polling failed with repeated HTTP ${response.status} responses`
        )
      }
      continue
    }

    consecutiveErrors = 0
    const job = await response.json()

    // Report progress
    onProgress(job.progress ?? 0, job.progressMessage ?? '')

    // Check terminal states
    if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
      return {
        jobId: job.jobId,
        status: job.status,
        progress: job.progress,
        resultPayload: job.resultPayload,
        errorMessage: job.errorMessage,
      }
    }
  }
}

/**
 * Fetch the current state of an async job without polling.
 *
 * Used when the user navigates back to a section after a job has already
 * completed — we need the final result but don't want to re-enter the
 * polling loop.
 */
export async function getJob(jobId: string, sessionId: string): Promise<AsyncJobResult> {
  const headers = await getAuthHeaders()
  const url = new URL(`${API_BASE_URL}/agent/jobs/${jobId}`, window.location.origin)
  url.searchParams.set('sessionId', sessionId)

  const response = await fetch(url.toString(), { method: 'GET', headers })
  if (!response.ok) {
    if (response.status === 404) {
      throw new AgentClientError('JOB_NOT_FOUND', 'Async job not found or expired')
    }
    const errorText = await response.text().catch(() => 'Unknown error')
    throw new AgentClientError(
      'POLL_FAILED',
      `Fetching job failed: ${response.status} ${errorText}`
    )
  }

  const job = await response.json()
  return {
    jobId: job.jobId,
    status: job.status,
    progress: job.progress,
    resultPayload: job.resultPayload,
    errorMessage: job.errorMessage,
  }
}

// ===================
// Cancel
// ===================

/**
 * Cancel an active agent turn or async job.
 */
export async function cancelAgentTurn(params: {
  turnId?: string
  sessionId?: string
}): Promise<{
  cancelled: boolean
  /** R1 F1′: a document job drafting this document, left running (Stop is for the turn). */
  documentJobKept?: string
}> {
  const headers = await getAuthHeaders()
  const url = new URL(`${API_BASE_URL}/agent/cancel`, window.location.origin)

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error')
    throw new AgentClientError('CANCEL_FAILED', `Cancel request failed: ${response.status} ${errorText}`)
  }

  return response.json()
}

// ===================
// Event Replay
// ===================

/**
 * Replay persisted events for a turn from a given sequence number.
 * Used for reconnection after client disconnect.
 */
export async function replayTurnEvents(
  turnId: string,
  afterSeq: number,
  onEvent: AgentEventHandler,
  signal?: AbortSignal
): Promise<void> {
  const headers = await getAuthHeaders()
  const url = new URL(
    `${API_BASE_URL}/agent/turns/${turnId}/events`,
    window.location.origin
  )
  if (afterSeq > 0) {
    url.searchParams.set('afterSeq', String(afterSeq))
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
    ...(signal ? { signal } : {}),
  })

  if (!response.ok) {
    if (response.status === 404) {
      throw new AgentClientError('TURN_NOT_FOUND', 'Turn not found')
    }
    const errorText = await response.text().catch(() => 'Unknown error')
    throw new AgentClientError('REPLAY_FAILED', `Event replay failed: ${response.status} ${errorText}`)
  }

  if (!response.body) {
    throw new AgentClientError('NO_BODY', 'Replay response body is null')
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''

  try {
    for (;;) {
      const { done, value } = await reader.read()

      if (done) break

      buffer += value
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed.length === 0) continue
        try {
          onEvent(JSON.parse(trimmed) as StreamEvent)
        } catch {
          // Skip malformed line
        }
      }
    }

    if (buffer.trim().length > 0) {
      try {
        onEvent(JSON.parse(buffer.trim()) as StreamEvent)
      } catch {
        // Skip malformed trailing line
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// NOTE: there is deliberately no saveSegment here. Segment content saves go
// through `saveSegment` in `@/lib/api` — the single chokepoint that strips
// suggestion diff markup (see stripDiffMarkup). A second, unstripped save
// path is how the diff-corruption bug (E2E F1, 2026-07-29) could return.

// ===================
// Error Class
// ===================

export class AgentClientError extends Error {
  code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'AgentClientError'
    this.code = code
  }
}

// ===================
// Utilities
// ===================

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms)

    if (signal) {
      const onAbort = () => {
        clearTimeout(timer)
        reject(new AgentClientError('CANCELLED', 'Operation was cancelled'))
      }

      if (signal.aborted) {
        clearTimeout(timer)
        reject(new AgentClientError('CANCELLED', 'Operation was cancelled'))
        return
      }

      signal.addEventListener('abort', onAbort, { once: true })

      // Clean up listener when timer fires
      const originalResolve = resolve
      resolve = () => {
        signal.removeEventListener('abort', onAbort)
        originalResolve()
      }
    }
  })
}

/**
 * Combine multiple abort signals into a single signal.
 * The combined signal aborts when any of the input signals abort.
 */
function combineAbortSignals(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController()

  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort()
      return controller.signal
    }

    signal.addEventListener('abort', () => {
      controller.abort()
    }, { once: true })
  }

  return controller.signal
}

function isStreamAbortError(error: unknown): boolean {
  if (!(error instanceof Error)) return false

  const code = 'code' in error && typeof (error as { code?: unknown }).code === 'string'
    ? ((error as { code: string }).code).toUpperCase()
    : ''

  if (code === 'ABORT_ERR' || code === 'ECONNRESET') return true

  const message = error.message.toLowerCase()
  return (
    message.includes('bodystreambuffer was aborted')
    || message.includes('body stream was aborted')
    || message.includes('stream was aborted')
    || message.includes('stream aborted')
  )
}
